import React, { useEffect, useState } from "react"
import API from "../../common/API"
import { parsedUser } from "../../common/GetCurrentUser"
import { Card, Col, Container, Row } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import TotalVisitMonthly from "./TotalVisitMonthly"
import TotalPlaceMonthly from "./TotalPlaceMonthly"
import VehicleByStatus from "./VehicleByStatus"
import VehicleByBrand from "./VehicleByBrand"
import { DashboardSkeleton } from "./SkeletonLoaders"
import TotalVehicleVisitMonthly from "./TotalVehicleVisitMonthly"
import { useHistory } from "react-router-dom"
import { Routes } from "@/routes"

function DashboardContainer() {
  const user = parsedUser()
  const history = useHistory()
  const { t } = useTranslation("global")
  const [generalInfo, setGeneralInfo] = useState(null)
  const [summaryByStage, setSummaryByStage] = useState([])
  const [summaryByStatus, setSummaryByStatus] = useState([])
  const [vehicleByGPSModel, setVehicleByGPSModel] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [quickStats, setQuickStats] = useState({
    today: null,
    week: null,
    month: null,
  })

  const [selectedUser, setSelectedUser] = useState(null)
  const [users, setUsers] = useState([])

  const requestUsers = async () => {
    try {
      let requestAPI = await API.getAction(
        "users/List?OrganizationId=" + (user?.OrganizationId || "")
      )

      if (requestAPI?.data?.status === "ok") {
        setUsers(requestAPI.data.response || [])
      }
    } catch (ex) {
      console.error("Error loading users:", ex)
    }
  }

  const bindDataRemote = async () => {
    try {
      let record = await API.getAction(
        "dashboard/general?organizationId=" + user.OrganizationId
      )
      if (record.data.status === "ok") {
        setGeneralInfo(record.data.response)
      }
    } catch (ex) {
      // Error logged
    } finally {
    }
  }

  const bindDataByStageRemote = async () => {
    try {
      let record = await API.getAction(
        "place/summaryByStage?organizationId=" +
          user.OrganizationId +
          ("&assignedTo=" + (selectedUser ?? ""))
      )
      if (record.data.status === "ok") {
        setSummaryByStage(record.data.response)
      }
    } catch (ex) {
      // Error logged
    } finally {
    }
  }

  const bindDataByStatusRemote = async () => {
    try {
      let record = await API.getAction(
        "place/summaryByStatus?organizationId=" +
          user.OrganizationId +
          ("&assignedTo=" + (selectedUser ?? ""))
      )
      if (record.data.status === "ok") {
        setSummaryByStatus(record.data.response)
      }
    } catch (ex) {
      // Error logged
    } finally {
    }
  }

  const bindDataByVehicleByGPSModelRemote = async () => {
    try {
      let record = await API.getAction(
        "agl/VehicleByGPSModel?organizationId=" + user.OrganizationId
      )
      if (record.data.status === "ok") {
        setVehicleByGPSModel(record.data.response)
      }
    } catch (ex) {
      // Error logged
    } finally {
    }
  }

  const fetchQuickStats = async (agentId = null) => {
    const periods = ["today", "week", "month"]

    try {
      const statsRequests = periods.map(period => {
        let query = `organizationId=${user.OrganizationId}&period=${period}`
        if (agentId) {
          query += `&agentId=${agentId}`
        }
        return API.getAction(`dashboard/QuickStats?${query}`)
      })

      const [todayRes, weekRes, monthRes] = await Promise.all(statsRequests)

      setQuickStats({
        today: todayRes?.data?.status === "ok" ? todayRes.data.response : null,
        week: weekRes?.data?.status === "ok" ? weekRes.data.response : null,
        month: monthRes?.data?.status === "ok" ? monthRes.data.response : null,
      })
    } catch (ex) {
      setQuickStats({
        today: null,
        week: null,
        month: null,
      })
    }
  }

  const safeRatio = (numerator, denominator, decimals = 1) => {
    if (!denominator) {
      return "0.0"
    }
    return (numerator / denominator).toFixed(decimals)
  }

  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true)
      await Promise.all([
        bindDataRemote(),
        bindDataByStageRemote(),
        bindDataByStatusRemote(),
        bindDataByVehicleByGPSModelRemote(),
        requestUsers(),
        fetchQuickStats(),
      ])
      setIsLoading(false)
    }
    loadInitialData()
  }, [])

  useEffect(() => {
    bindDataRemote()
    bindDataByStageRemote()
    bindDataByStatusRemote()
    fetchQuickStats(selectedUser)
  }, [selectedUser])

  const StatCard = ({ title, value, icon, type, isLoading, delay = 0 }) => (
    <Col xs={6} lg={3} className={`mb-4 fade-in-delay-${delay}`}>
      <Card className={`dashboard-stats-card ${type} h-100`}>
        <Card.Header>
          <Card.Title
            className={isLoading ? "loading-pulse" : "counter-animate"}
          >
            {isLoading ? "..." : value || 0}
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <i className={`${icon} me-2`}></i>
          {title}
        </Card.Body>
      </Card>
    </Col>
  )

  // Show skeleton while loading
  if (isLoading) {
    return <DashboardSkeleton />
  }

  return (
    <div className="dashboard-container fade-in">
      <Container>
        {/* General Info Section */}
        <div className="dashboard-section-header text-white">
          <h4>{t("General info")}</h4>
        </div>

        <Row>
          <StatCard
            title={t("Places")}
            value={generalInfo?.totalPlaces}
            icon="fa fa-map-location"
            type="places"
            isLoading={false}
            delay={0}
          />
          <StatCard
            title={t("Vehicles")}
            value={generalInfo?.totalVehicles}
            icon="fa fa-car"
            type="vehicles"
            isLoading={false}
            delay={1}
          />
          <StatCard
            title={t("Agentes")}
            value={generalInfo?.totalUsers}
            icon="fa fa-user"
            type="agents"
            isLoading={false}
            delay={2}
          />
          <StatCard
            title={t("Visits")}
            value={generalInfo?.totalVisits}
            icon="fa fa-calendar"
            type="visits"
            isLoading={false}
            delay={3}
          />
        </Row>

        <div className="dashboard-section-header text-white">
          <h4>{t("Indicadores operativos")}</h4>
        </div>

        <Row>
          <StatCard
            title={t("Visitas hoy")}
            value={quickStats?.today?.visitsCount ?? 0}
            icon="fa fa-calendar-day"
            type="today-visits"
            isLoading={false}
            delay={0}
          />
          <StatCard
            title={t("Visitas esta semana")}
            value={quickStats?.week?.visitsCount ?? 0}
            icon="fa fa-calendar-week"
            type="week-visits"
            isLoading={false}
            delay={1}
          />
          <StatCard
            title={t("Lugares nuevos del mes")}
            value={quickStats?.month?.placesCount ?? 0}
            icon="fa fa-map-pin"
            type="month-places"
            isLoading={false}
            delay={2}
          />
          <StatCard
            title={t("Visitas por lugar")}
            value={safeRatio(generalInfo?.totalVisits ?? 0, generalInfo?.totalPlaces ?? 0)}
            icon="fa fa-gauge"
            type="visit-efficiency"
            isLoading={false}
            delay={3}
          />
        </Row>

        {/* Agent Info Section */}
        <div className="dashboard-section-header text-white">
          <h4>{t("Agent Info.")}</h4>
        </div>

        <Row>
          <Col xs={12}>
            <Card className="dashboard-filter-card fade-in">
              <Card.Header>
                <Card.Title>{t("Filter")}</Card.Title>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col xs={12} md={4} xl={3}>
                    <label className="form-label fw-semibold">
                      {t("Agent")}
                    </label>
                    <select
                      className="form-control"
                      value={selectedUser}
                      onChange={e => setSelectedUser(e.target.value)}
                    >
                      <option value="">{t("All")}</option>
                      {users &&
                        users.map((item, index) => {
                          return (
                            <option key={index} value={item.id}>
                              {item.firstName + " " + (item.lastName ?? "")}
                            </option>
                          )
                        })}
                    </select>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Summary Tables */}
        <Row>
          <Col xs={12} md={6} className="fade-in">
            <Card className="dashboard-table-card">
              <Card.Header>
                <Card.Title>{t("Resumen por etapa")}</Card.Title>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <table className="dashboard-table table">
                    <tbody>
                      {summaryByStage.map((item, index) => (
                        <tr key={index}>
                          <td
                            className="clickable"
                            onClick={() => {
                              history.push(
                                Routes.Search.path + `?stage=${item.id}`
                              )
                            }}
                          >
                            <i className={item.icon + " me-2"}></i>
                            {item.name}
                          </td>
                          <td className="text-end">{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} md={6} className="fade-in">
            <Card className="dashboard-table-card">
              <Card.Header>
                <Card.Title>{t("Resumen Lugares por estatus")}</Card.Title>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <table className="dashboard-table table">
                    <tbody>
                      {summaryByStatus.map((item, index) => (
                        <tr key={index}>
                          <td
                            className="clickable"
                            onClick={() => {
                              history.push(
                                Routes.Search.path + `?status=${item.id}`
                              )
                            }}
                          >
                            <i
                              className={item.icon + " me-2"}
                              style={{ color: item.color }}
                            ></i>
                            {item.name}
                          </td>
                          <td className="text-end">{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Charts Section */}
        <Row className="my-4">
          <Col xs={12} md={6} className="fade-in-delay-1">
            <TotalVisitMonthly agent={selectedUser} />
          </Col>
          <Col xs={12} md={6} className="fade-in-delay-2">
            <TotalPlaceMonthly agent={selectedUser} />
          </Col>
        </Row>

        {/* Vehicles Section */}
        <div className="dashboard-section-header text-white">
          <h4>{t("Vehicles")}</h4>
        </div>

        <Row>
          <Col xs={12} md={6} lg={8} className="fade-in">
            <TotalVehicleVisitMonthly />
            <VehicleByStatus />
            <Card className="dashboard-table-card">
              <Card.Header>
                <Card.Title>{t("GPS")}</Card.Title>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <table className="dashboard-table table">
                    <tbody>
                      {vehicleByGPSModel.map((item, index) => (
                        <tr key={index}>
                          <td>{item.brand}</td>
                          <td>{item.model}</td>
                          <td className="text-end">{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} md={6} lg={4} className="fade-in-delay-1">
            <VehicleByBrand />
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default DashboardContainer
